
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRememberedState } from '../../hooks/useRemembered';
import { EmailLog, UserProfile } from '../../types';
import { fetchEmailLogs } from '../../services/cloudService';
import { supabase } from '../../services/supabaseClient';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { gesperrtZusatz } from '../../services/gesperrteKonten';
import { Mail, CheckCircle2, XCircle, Search, User, Clock, RefreshCw, Loader2, Building2, Info, Eye, X } from 'lucide-react';
import { Button } from '../Button';
import { ladeUebungsProjektIds } from '../../services/uebung/uebungService';
import {
    SENDE_STANDARD,
    SENDE_VOREINSTELLUNGEN,
    alsBenutzerZeitraum,
    gueltigeVoreinstellung,
    ladefehlerText,
    zeitraumGrenzen,
    zeitraumText,
} from './adminZeitraum';
import type { BenutzerZeitraum, SendeVoreinstellung, ZeitraumGrenzen } from './adminZeitraum';

const KEINE_LOGS: EmailLog[] = [];

type Ladestand =
    | { schluessel: string; art: 'ok'; logs: EmailLog[]; grenzen: ZeitraumGrenzen }
    | { schluessel: string; art: 'fehler'; meldung: string };

/** Wofuer ein Ladestand gilt. Die Von/Bis-Felder zaehlen nur bei „Benutzerdefiniert“. */
const ladeSchluessel = (voreinstellung: SendeVoreinstellung, felder: BenutzerZeitraum, neuladen: number): string =>
    voreinstellung === 'benutzerdefiniert'
        ? `${voreinstellung}|${felder.start}|${felder.end}#${neuladen}`
        : `${voreinstellung}#${neuladen}`;

/**
 * Pause nach einer Eingabe in Von/Bis, bevor geladen wird: ein getipptes
 * Jahr ist sonst je Ziffer ein gueltiges Datum (0002, 0020, 0202, 2026) und
 * jedes eine eigene Abfrage.
 */
const TIPP_PAUSE_MS = 400;

export const SentReportsView: React.FC = () => {
    const [users, setUsers] = useState<UserProfile[]>([]);
    // Filter über Vergangenes: Gesperrte (Nr. 148) bleiben wählbar, markiert.
    const gesperrteKonten = useGesperrteKonten();
    const [searchTerm, setSearchTerm] = useRememberedState('admin.sent.search', '');

    // Filters
    const [senderFilter, setSenderFilter] = useRememberedState<string>('admin.sent.sender', '');
    // Von/Bis von „Benutzerdefiniert“ ('YYYY-MM-DD', Bis einschließlich) —
    // derselbe Schlüssel wie früher der Datumsfilter im Browser.
    const [dateRange, setDateRange] = useRememberedState<BenutzerZeitraum>('admin.sent.range', { start: '', end: '' });
    // Wer vor dem Umbau schon Von/Bis gesetzt hatte, landet wieder darauf.
    const [gemerkt, setVoreinstellung] = useRememberedState<SendeVoreinstellung>(
        'admin.sent.zeitraum',
        () => (dateRange?.start || dateRange?.end ? 'benutzerdefiniert' : SENDE_STANDARD),
    );
    const voreinstellung = gueltigeVoreinstellung(gemerkt, SENDE_VOREINSTELLUNGEN, SENDE_STANDARD);
    const von = dateRange?.start ?? '';
    const bis = dateRange?.end ?? '';

    // Laden
    const [neuladen, setNeuladen] = useState(0);
    const [stand, setStand] = useState<Ladestand | null>(null);

    // Modal
    const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

    // Der Zeitraum wird in der DATENBANK gefiltert (fetchEmailLogs, seitenweise)
    // — bis Oktober 2026 kamen die neuesten 1000 Berichte und der Browser
    // filterte darin. Neu geladen wird bei jedem Zeitraumwechsel. Kein setState
    // im Effektrumpf (react-hooks/set-state-in-effect): „lädt“ ergibt sich
    // daraus, dass der letzte Stand zu einem anderen Schlüssel gehört.
    useEffect(() => {
        let abgebrochen = false;
        const felder = { start: von, end: bis };
        const schluessel = ladeSchluessel(voreinstellung, felder, neuladen);
        const timer = setTimeout(() => {
            const grenzen = zeitraumGrenzen(voreinstellung, new Date(), felder);
            void (async () => {
                try {
                    const [data, uebung, { data: userData }] = await Promise.all([
                        fetchEmailLogs(grenzen),
                        // ÜBUNGSZEILEN GEHÖREN NICHT IN DEN SENDEVERLAUF (Plan 8.2).
                        // Im Regelfall gibt es sie gar nicht — weder der Sendeschutz noch
                        // der Trigger `email_logs_kein_uebungsversand` (Nr. 100) lassen
                        // einen Versand aus der Übung zu. Diese Zeile ist der Riegel für
                        // den Fall, dass doch einmal etwas durchrutscht: `email_logs` hat
                        // keine eigene Übungsspalte, gefiltert wird deshalb über die
                        // Projekt-ids. Fällt die Abfrage aus, kommt eine leere Menge und
                        // es wird nichts ausgeblendet.
                        ladeUebungsProjektIds(),
                        // Fetch users for sender mapping
                        supabase.from('profiles').select('*'),
                    ]);
                    if (abgebrochen) return;
                    setStand({
                        schluessel,
                        art: 'ok',
                        grenzen,
                        logs: uebung.size === 0 ? data : data.filter(l => !uebung.has(l.project_id)),
                    });
                    if (userData) setUsers(userData as UserProfile[]);
                } catch (e) {
                    // Sichtbar statt nur console.error: ein leerer Verlauf
                    // behauptete sonst, es sei nichts versendet worden.
                    if (!abgebrochen) setStand({ schluessel, art: 'fehler', meldung: ladefehlerText(e) });
                }
            })();
        }, voreinstellung === 'benutzerdefiniert' ? TIPP_PAUSE_MS : 0);
        return () => {
            abgebrochen = true;
            clearTimeout(timer);
        };
    }, [voreinstellung, von, bis, neuladen]);

    const geladen = stand && stand.schluessel === ladeSchluessel(voreinstellung, { start: von, end: bis }, neuladen) ? stand : null;
    const loading = geladen === null;
    const ladefehler = geladen?.art === 'fehler' ? geladen.meldung : null;
    const logs = geladen?.art === 'ok' ? geladen.logs : KEINE_LOGS;
    const zeitraum = geladen?.art === 'ok' ? zeitraumText(geladen.grenzen) : '';

    const neuLaden = useCallback(() => setNeuladen(n => n + 1), []);

    const waehleVoreinstellung = (wert: SendeVoreinstellung) => {
        // Wechsel auf „Benutzerdefiniert“ mit leeren Feldern: mit dem bisher
        // gewählten Zeitraum vorbelegen — leere Felder hießen „alles laden“.
        if (wert === 'benutzerdefiniert' && !von && !bis) {
            setDateRange(alsBenutzerZeitraum(voreinstellung, new Date()));
        }
        setVoreinstellung(wert);
    };

    const zeitraumZuruecksetzen = () => {
        setDateRange({ start: '', end: '' });
        setVoreinstellung(SENDE_STANDARD);
    };

    const getSenderName = (id: string) => {
        const u = users.find(user => user.id === id);
        return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : 'Unbekannt';
    };

    const filteredLogs = useMemo(() => {
        let res = logs;

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            res = res.filter(l => 
                l.project_name.toLowerCase().includes(lower) || 
                l.subject.toLowerCase().includes(lower)
            );
        }

        if (senderFilter) {
            res = res.filter(l => l.sender_id === senderFilter);
        }

        // Der Zeitraum ist schon in der Abfrage gefiltert (siehe Laden).
        return res;
    }, [logs, searchTerm, senderFilter]);

    const formatDate = (d: string) => new Date(d).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

    return (
        <div className="space-y-6 animate-fade-in relative">
            {/* DETAILS MODAL */}
            {selectedLog && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                    E-Mail Details
                                </h3>
                                <p className="text-xs text-slate-500 mt-1">ID: {selectedLog.id}</p>
                            </div>
                            <button onClick={() => setSelectedLog(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                                <X className="w-5 h-5 text-slate-500" />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <label className="text-[11px] uppercase font-bold text-slate-400 tracking-wider block mb-1">Status</label>
                                    {selectedLog.status === 'success' ? (
                                        <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                                            <CheckCircle2 className="w-4 h-4" /> Erfolgreich versendet
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-red-600 font-bold text-sm">
                                            <XCircle className="w-4 h-4" /> Fehler beim Senden
                                        </div>
                                    )}
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <label className="text-[11px] uppercase font-bold text-slate-400 tracking-wider block mb-1">Zeitpunkt</label>
                                    <div className="text-slate-700 font-medium text-sm flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-slate-400" />
                                        {formatDate(selectedLog.sent_at)}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-[11px] uppercase font-bold text-slate-400 tracking-wider block mb-1">Versender</label>
                                    <div className="text-sm font-medium text-slate-800 p-3 bg-white border border-slate-200 rounded-lg flex items-center gap-2">
                                        <User className="w-4 h-4 text-slate-400" />
                                        {getSenderName(selectedLog.sender_id)}
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-[11px] uppercase font-bold text-slate-400 tracking-wider block mb-1">Empfänger</label>
                                    <div className="text-sm text-slate-600 p-3 bg-white border border-slate-200 rounded-lg break-all">
                                        {selectedLog.recipients}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-[11px] uppercase font-bold text-slate-400 tracking-wider block mb-1">Betreff</label>
                                    <div className="text-sm font-bold text-slate-800 p-3 bg-white border border-slate-200 rounded-lg">
                                        {selectedLog.subject}
                                    </div>
                                </div>

                                {selectedLog.error_message && (
                                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
                                        <label className="text-[11px] uppercase font-bold text-red-400 tracking-wider block mb-1">Fehlermeldung</label>
                                        <p className="text-red-700 text-sm font-mono">{selectedLog.error_message}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                            <Button variant="secondary" onClick={() => setSelectedLog(null)}>Schließen</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* FILTERS & SEARCH */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input 
                            type="text" 
                            placeholder="Suchen (Projekt, Betreff...)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>
                    
                    <div className="flex items-center gap-2 min-w-[200px]">
                        <User className="w-4 h-4 text-slate-400" />
                        <select 
                            value={senderFilter} 
                            onChange={(e) => setSenderFilter(e.target.value)}
                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        >
                            <option value="">Alle Versender</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name} ({u.company_name || 'Keine Firma'}){gesperrtZusatz(u.id, gesperrteKonten)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={neuLaden}
                        className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 hover:text-blue-600 transition-all"
                        title="Aktualisieren"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <div className="flex flex-col md:flex-row md:flex-wrap gap-4 items-center pt-2 border-t border-slate-100">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-2">Zeitraum:</span>
                    <div className="flex flex-wrap items-center gap-1">
                        {SENDE_VOREINSTELLUNGEN.map(o => {
                            const aktiv = o.wert === voreinstellung;
                            return (
                                <button
                                    key={o.wert}
                                    type="button"
                                    onClick={() => waehleVoreinstellung(o.wert)}
                                    aria-pressed={aktiv}
                                    className={`h-8 px-3 rounded-full text-[13px] font-semibold whitespace-nowrap ${aktiv ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                    {voreinstellung === 'benutzerdefiniert' && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={von}
                                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs"
                                title="Von (einschließlich)"
                            />
                            <span className="text-slate-300">-</span>
                            <input
                                type="date"
                                value={bis}
                                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs"
                                title="Bis (einschließlich)"
                            />
                        </div>
                    )}
                    {zeitraum && (
                        <span className="text-xs text-slate-500">
                            <span className="font-semibold text-slate-700">{zeitraum}</span> · {logs.length.toLocaleString('de-DE')} im Zeitraum
                        </span>
                    )}
                    {voreinstellung !== SENDE_STANDARD && (
                        <button
                            onClick={zeitraumZuruecksetzen}
                            className="text-xs text-red-500 hover:text-red-600 font-medium ml-auto md:ml-0"
                        >
                            Zeitraum zurücksetzen
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Zeitpunkt</th>
                                <th className="px-6 py-4">Projekt</th>
                                <th className="px-6 py-4">Versender</th>
                                <th className="px-6 py-4">Betreff</th>
                                <th className="px-6 py-4 text-right">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center">
                                        <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
                                        <p className="text-slate-500 font-medium">Lade Sendeprotokolle...</p>
                                    </td>
                                </tr>
                            ) : ladefehler ? (
                                <tr>
                                    <td colSpan={6} className="py-16 px-6 text-center">
                                        <XCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
                                        <p className="font-bold text-red-700">Sendeprotokolle konnten nicht geladen werden</p>
                                        <p className="text-xs text-red-600 font-mono mt-1 break-all">{ladefehler}</p>
                                        <button
                                            type="button"
                                            onClick={neuLaden}
                                            className="mt-4 px-4 py-2 bg-white border border-red-200 rounded-lg text-xs font-bold text-red-700 hover:bg-red-50 transition-colors"
                                        >
                                            Erneut versuchen
                                        </button>
                                    </td>
                                </tr>
                            ) : filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center text-slate-400">
                                        <Mail className="w-12 h-12 mx-auto mb-4 opacity-20" />
                                        <p className="font-bold">Keine Sendevorgänge gefunden</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredLogs.map((log) => (
                                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            {log.status === 'success' ? (
                                                <div className="flex items-center gap-1.5 text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100 w-fit">
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                    <span>Erfolgreich</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 text-red-600 font-bold text-xs bg-red-50 px-2 py-1 rounded-full border border-red-100 w-fit">
                                                    <XCircle className="w-3.5 h-3.5" />
                                                    <span>Fehler</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-xs text-slate-600 flex items-center gap-2">
                                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                                {formatDate(log.sent_at)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                                <Building2 className="w-3.5 h-3.5 text-blue-500" />
                                                {log.project_name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-xs text-slate-700 font-medium flex items-center gap-2">
                                                <User className="w-3.5 h-3.5 text-slate-400" />
                                                {getSenderName(log.sender_id)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-xs text-slate-700 font-medium italic truncate max-w-[200px]">
                                                "{log.subject}"
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button 
                                                onClick={() => setSelectedLog(log)}
                                                className="p-2 hover:bg-blue-50 text-slate-400 hover:text-blue-600 rounded-lg transition-colors"
                                                title="Details anzeigen"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 p-5 rounded-2xl flex items-start gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm text-blue-600">
                    <Info className="w-6 h-6" />
                </div>
                <div>
                    <h4 className="font-bold text-blue-900 text-sm">Hinweise zum Sendestatus</h4>
                    <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                        Erfolgreich bedeutet, dass der Strato SMTP-Server die E-Mail zur Zustellung angenommen hat. 
                        Sollte ein Empfänger den Bericht dennoch nicht erhalten, prüfen Sie bitte dessen Spam-Ordner oder die Korrektheit der E-Mail Adresse.
                    </p>
                </div>
            </div>
        </div>
    );
};
